import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import WorkCard from "@/components/feature/WorkCard";
import NewsCard from "@/components/feature/NewsCard";
import { getFeaturedWorks, searchNews, searchWorks } from "@/utils/media";
import { useSeo } from "@/hooks/useSeo";

export default function Search() {
  const [params, setParams] = useSearchParams();
  const keyword = params.get("keyword") || "";
  const [input, setInput] = useState(keyword);
  const [typeFilter, setTypeFilter] = useState<"all" | "movie" | "tv">("all");
  const [yearFilter, setYearFilter] = useState<number | 0>(0);

  useSeo({
    title: keyword ? `搜索“${keyword}” - 挣扎吧亚当君` : "搜索影视作品与资讯 - 挣扎吧亚当君",
    description:
      "在挣扎吧亚当君搜索影视作品与影视资讯，支持按影视类型、年份筛选结果，快速找到你感兴趣的内容。",
    keywords: "影视搜索,电影搜索,电视剧搜索,影视资讯搜索,挣扎吧亚当君",
  });

  useEffect(() => {
    setInput(keyword);
    setTypeFilter("all");
    setYearFilter(0);
  }, [keyword]);

  const workResults = useMemo(() => {
    let list = searchWorks(keyword);
    if (typeFilter !== "all") list = list.filter((w) => w.type === typeFilter);
    if (yearFilter) list = list.filter((w) => w.year === yearFilter);
    return list;
  }, [keyword, typeFilter, yearFilter]);

  const newsResults = useMemo(() => searchNews(keyword), [keyword]);

  const years = useMemo(() => {
    const set = new Set(searchWorks(keyword).map((w) => w.year));
    return Array.from(set).sort((a, b) => b - a);
  }, [keyword]);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const next = new URLSearchParams();
    if (input.trim()) next.set("keyword", input.trim());
    setParams(next);
  };

  const hasKeyword = keyword.trim().length > 0;
  const noResults = hasKeyword && workResults.length === 0 && newsResults.length === 0;
  const recommendations = getFeaturedWorks(4);

  return (
    <div className="w-full px-4 md:px-8 py-6 md:py-10">
      <div className="max-w-3xl mx-auto text-center">
        <h1 className="font-heading text-2xl md:text-3xl font-black text-foreground-950">搜索影视作品与资讯</h1>
        <p className="mt-3 text-sm text-foreground-600">
          支持模糊匹配作品标题、导演、主演、类型，以及资讯标题与摘要。
        </p>
        <form onSubmit={submit} className="mt-6 flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-400 text-lg"></i>
            <input
              type="text"
              name="keyword"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="输入作品名、导演、主演或资讯关键词…"
              aria-label="搜索关键词"
              className="w-full h-12 pl-11 pr-4 rounded-md bg-background-50 border border-background-200/80 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
            />
          </div>
          <button
            type="submit"
            className="h-12 px-8 rounded-md bg-primary-500 text-background-50 text-sm font-bold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            搜索
          </button>
        </form>
      </div>

      {hasKeyword ? (
        <div className="mt-10">
          <p className="text-sm text-foreground-600">
            关键词 <strong className="text-foreground-950">“{keyword}”</strong> 共找到{" "}
            <strong className="text-accent-600">{workResults.length + newsResults.length}</strong> 条结果
          </p>

          {!noResults && (
            <div className="mt-5 flex flex-wrap items-center gap-4">
              <div className="flex p-1 rounded-full bg-background-200/70">
                {[
                  { k: "all", label: "全部" },
                  { k: "movie", label: "电影" },
                  { k: "tv", label: "电视剧" },
                ].map((t) => (
                  <button
                    key={t.k}
                    type="button"
                    onClick={() => setTypeFilter(t.k as typeof typeFilter)}
                    className={`px-4 py-1.5 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap ${
                      typeFilter === t.k ? "bg-primary-500 text-background-50" : "text-foreground-600"
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
              {years.length > 0 && (
                <label className="flex items-center gap-2 h-9 px-4 rounded-md bg-background-50 border border-background-200/80 text-sm text-foreground-600">
                  <i className="ri-calendar-line"></i>
                  <select
                    aria-label="按年份筛选"
                    value={yearFilter}
                    onChange={(e) => setYearFilter(Number(e.target.value))}
                    className="bg-transparent text-sm text-foreground-800 outline-none cursor-pointer"
                  >
                    <option value={0}>全部年份</option>
                    {years.map((y) => (
                      <option key={y} value={y}>
                        {y} 年
                      </option>
                    ))}
                  </select>
                </label>
              )}
            </div>
          )}

          {noResults ? (
            <div className="mt-8 py-16 text-center bg-background-50 rounded-lg border border-dashed border-background-300">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-500">
                <i className="ri-emotion-sad-line text-3xl"></i>
              </div>
              <h2 className="font-heading text-lg font-bold text-foreground-950">没有找到与“{keyword}”相关的内容</h2>
              <p className="mt-2 text-sm text-foreground-500">试试更换关键词，或从下方推荐中挑选。</p>
              <section className="mt-10 text-left">
                <h3 className="font-heading text-base font-bold text-foreground-950 mb-4 text-center">为你推荐</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {recommendations.map((w) => (
                    <WorkCard key={w.id} work={w} />
                  ))}
                </div>
              </section>
            </div>
          ) : (
            <div className="space-y-12 mt-8">
              {workResults.length > 0 && (
                <section>
                  <div className="flex items-center gap-2 mb-5">
                    <span className="w-6 h-[3px] rounded-full bg-accent-500"></span>
                    <h2 className="font-heading text-lg font-bold text-foreground-950">
                      影视作品（{workResults.length}）
                    </h2>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
                    {workResults.map((w) => (
                      <WorkCard key={w.id} work={w} />
                    ))}
                  </div>
                </section>
              )}

              {newsResults.length > 0 && (
                <section>
                  <div className="flex items-center gap-2 mb-5">
                    <span className="w-6 h-[3px] rounded-full bg-accent-500"></span>
                    <h2 className="font-heading text-lg font-bold text-foreground-950">
                      影视资讯（{newsResults.length}）
                    </h2>
                  </div>
                  <div className="space-y-5">
                    {newsResults.map((n) => (
                      <NewsCard key={n.id} item={n} layout="row" />
                    ))}
                  </div>
                </section>
              )}
            </div>
          )}
        </div>
      ) : (
        <div className="mt-12">
          <h2 className="font-heading text-lg font-bold text-foreground-950 mb-5">热门推荐</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
            {recommendations.map((w) => (
              <WorkCard key={w.id} work={w} />
            ))}
          </div>
          <p className="mt-8 text-center text-sm text-foreground-500">
            想看更多？前往
            <Link to="/category/movie" className="text-primary-600 mx-1 hover:underline">
              电影分类
            </Link>
            或
            <Link to="/category/tv" className="text-primary-600 mx-1 hover:underline">
              电视剧分类
            </Link>
            浏览。
          </p>
        </div>
      )}
    </div>
  );
}